'use client';

import { IconName } from '@swisscom/sdx';
import React, { forwardRef, useCallback, useState } from 'react';

type OptionalProps = {
  children?: string;
  disabled?: boolean;
  iconName?: IconName;
  iconSize?: number;
  isLoading?: boolean;
  color?: string;
  href?: string;
  theme?: 'primary' | 'secondary' | 'cancel' | 'confirm' | 'transparent';
  onClick?: () => MaybePromise<void>;
  indictatorColor?: string;
  badge?: string;
  preventPropagation?: boolean;
  background?: 'dark' | 'light';
};

type ButtonProps = OptionalProps;

/**
 * Button component using SDX button
 */
const Button = forwardRef<HTMLSdxButtonElement, OptionalProps>(
  (
    {
      disabled,
      iconName,
      iconSize,
      isLoading,
      children,
      onClick,
      theme,
      color,
      href,
      badge,
      preventPropagation,
      background,
    },
    ref,
  ) => {
    const [implicitLoading, setImplicitLoading] = useState<boolean>(false);
    const onClickProxy = useCallback(async (): Promise<void> => {
      const handler = onClick?.();
      if (!(handler instanceof Promise)) return;

      setImplicitLoading(true);
      try {
        await handler;
      } finally {
        setImplicitLoading(false);
      }
    }, [onClick]);

    return (
      <div>
        <sdx-button
          badge={badge}
          href={href}
          ref={ref}
          style={{ color }}
          background={background}
          label={children}
          onClick={(e: React.MouseEvent) => {
            if (preventPropagation) {
              e.preventDefault();
              e.stopPropagation();
            }

            void onClickProxy();
          }}
          disabled={disabled}
          icon-name={iconName}
          icon-size={iconSize}
          theme={theme ?? 'primary'}
          loading={isLoading || implicitLoading}
        />
      </div>
    );
  },
);

Button.displayName = 'Button';

export default Button;
export type { ButtonProps };
